import React, { useEffect } from 'react';
import {
  MessageCircle,
  Check,
  Mail,
  ShoppingCart,
  Package,
  Clock,
  AlertCircle,
  ArrowRight,
  Star
} from 'lucide-react';

const ForEcommerce = () => {
  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'Customer Support for E-commerce Shops | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Handle order questions, shipping inquiries & returns efficiently. Gmail, Facebook, WhatsApp ticketing for e-commerce. Trustpilot review alerts included. $29/month.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/for-ecommerce';

    // Structured data for e-commerce focus
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "TicketDesk for E-commerce",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "29",
        "priceCurrency": "USD"
      },
      "description": "Customer support software for e-commerce shops. Handle order questions, shipping inquiries, and returns from Gmail, Facebook, and WhatsApp in one place. Includes Trustpilot review monitoring.",
      "audience": {
        "@type": "Audience",
        "audienceType": "E-commerce businesses"
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  const handleStartFreeClick = (location) => {
    trackEvent('start_free_click', {
      button_location: location,
      page: 'for_ecommerce',
      value: 1
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center bg-purple-50 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Built for E-commerce
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Customer Support Software for E-commerce Shops
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Handle order questions, shipping inquiries, and return requests efficiently. All your customer messages from Gmail, Facebook, and WhatsApp in one simple inbox. Plus instant Trustpilot review alerts.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
                <a
                  href="/register"
                  onClick={() => handleStartFreeClick('hero_section')}
                  className="inline-flex items-center justify-center bg-purple-600 text-white px-5 sm:px-6 py-3 rounded text-sm sm:text-base font-medium hover:bg-purple-700 text-center"
                >
                  Start Free in 60 Seconds
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <a
                  href="https://calendly.com/hello-ticketdesk/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-5 sm:px-6 py-3 rounded text-sm sm:text-base font-medium hover:border-gray-400 text-center"
                >
                  Book a Demo
                </a>
              </div>

              <p className="text-xs sm:text-sm text-gray-500">No credit card required • Free plan available • Paid plans start at $29/month</p>
            </div>

            <div className="mt-8 lg:mt-0">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="bg-white rounded-xl shadow-md p-6 mb-4 border-l-4 border-blue-500">
                  <div className="flex items-center mb-2">
                    <Mail className="w-5 h-5 text-blue-600 mr-3" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">Order #4721 - Where is it?</div>
                      <div className="text-xs text-gray-500">Sarah M. • Gmail • 2 min ago</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 mb-4">
                  <div className="flex items-center mb-2">
                    <MessageCircle className="w-5 h-5 text-blue-500 mr-3" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">Can I return this?</div>
                      <div className="text-xs text-gray-500">Mike W. • Facebook • 5 min ago</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
                  <div className="flex items-center mb-2">
                    <Star className="w-5 h-5 text-red-500 fill-current mr-3" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-red-700">1-Star Review Alert</div>
                      <div className="text-xs text-gray-500">Lisa P. • Trustpilot • Just now</div>
                    </div>
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              E-commerce Support Challenges We Solve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Running an online shop means handling hundreds of customer questions across multiple channels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Order Status Questions</h3>
              <p className="text-sm text-gray-600">
                "Where is my order?" emails flooding your Gmail inbox every day
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Shipping Delays</h3>
              <p className="text-sm text-gray-600">
                Customers messaging on Facebook and WhatsApp about late deliveries
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Product Questions</h3>
              <p className="text-sm text-gray-600">
                Pre-sale inquiries scattered across email, Facebook, and your website
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Negative Reviews</h3>
              <p className="text-sm text-gray-600">
                Missing critical Trustpilot reviews that need immediate responses
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                One Inbox for All Your Customer Messages
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Stop switching between Gmail, Facebook Messenger, and WhatsApp Business. Every customer question becomes a trackable ticket your team can manage together.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Gmail, Facebook & WhatsApp in one place</div>
                    <div className="text-gray-600">See all order questions, shipping inquiries, and returns together</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Assign tickets to your team</div>
                    <div className="text-gray-600">Split customer support work - no more forwarding emails</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Nothing falls through the cracks</div>
                    <div className="text-gray-600">Track every question from received to resolved automatically</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Setup in 60 seconds</div>
                    <div className="text-gray-600">Connect Gmail with one click - start handling tickets immediately</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-12">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-blue-500">
                  <div className="text-sm font-semibold text-gray-900 mb-2">All channels unified</div>
                  <div className="text-xs text-gray-600">Gmail, Facebook, WhatsApp, Website Widget</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Assign to team members</div>
                  <div className="text-xs text-gray-600">Unlimited users included at no extra cost</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">AI polishes your replies</div>
                  <div className="text-xs text-gray-600">Sound professional in every response</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Monitoring Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-12">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-500">
                  <div className="flex items-center mb-2">
                    <Star className="w-5 h-5 text-red-500 fill-current mr-2" />
                    <div className="text-sm font-semibold text-red-700">1-Star Alert</div>
                  </div>
                  <div className="text-xs text-gray-600">"Product arrived damaged"</div>
                  <button className="mt-3 text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium">
                    Respond Now
                  </button>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Protect your reputation</div>
                  <div className="text-xs text-gray-600">Respond within hours, not days</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Instant Trustpilot Review Alerts
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Never miss a negative review again. Get immediate notifications for Trustpilot reviews under 3 stars and respond before it hurts sales.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Instant Trustpilot alerts</div>
                    <div className="text-gray-600">Know immediately when someone leaves a review</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Focus on negative reviews</div>
                    <div className="text-gray-600">Prioritize reviews under 3 stars that need responses</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Turn unhappy customers around</div>
                    <div className="text-gray-600">Fast responses can save sales and your reputation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Simple Pricing for E-commerce Shops
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            No per-seat pricing. Add your whole team for free.
          </p>

          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-8 max-w-md mx-auto">
            <div className="text-sm font-semibold text-purple-700 mb-2">STARTER PLAN</div>
            <div className="text-5xl font-bold text-purple-600 mb-2">$29</div>
            <div className="text-gray-600 mb-4">per month</div>
            <div className="text-sm text-gray-700 mb-6">
              200 tickets/month • Unlimited reviews • Unlimited team members
            </div>
            <a
              href="/register"
              onClick={() => handleStartFreeClick('pricing_section')}
              className="inline-flex items-center justify-center bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 w-full"
            >
              Start Free Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
            <p className="text-xs text-gray-500 mt-4">7-day free trial • No credit card required</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-6 max-w-lg mx-auto mb-8">
            <div className="text-sm text-gray-700 mb-2">Also available:</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Free Forever Plan</div>
            <div className="text-sm text-gray-600">10 tickets/month • Perfect for testing or micro businesses</div>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              Cancel anytime
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              No setup fees
            </div>
            <div className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2" />
              30-day money back
            </div>
          </div>

          <div className="mt-8">
            <a href="/#pricing" className="text-purple-600 hover:text-purple-700 font-medium">
              View all plans →
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Handle Support Like a Pro?
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            Join hundreds of e-commerce shops using TicketDesk to manage customer support
          </p>
          <a
            href="/register"
            onClick={() => handleStartFreeClick('final_cta')}
            className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
          >
            Start Free in 60 Seconds
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Customer support software for e-commerce shops. Gmail, Facebook, WhatsApp ticketing with Trustpilot review monitoring.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Solutions</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce Shops</a></li>
                <li><a href="/for-saas" className="hover:text-white">For SaaS Companies</a></li>
                <li><a href="/for-agencies" className="hover:text-white">For Agencies</a></li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2"></div>

            <div>
              <h3 className="font-semibold mb-4">Compare</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/compare/freshdesk" className="hover:text-white">vs Freshdesk</a></li>
                <li><a href="/compare/email" className="hover:text-white">vs Email</a></li>
                <li><a href="/pricing-comparison" className="hover:text-white">Pricing Comparison</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Customer support software for e-commerce shops. Handle order questions, shipping inquiries & returns. Trustpilot review monitoring included.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForEcommerce;
